import type { InfiniteData } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { useTickets } from 'contexts/TicketsContext';
import type { GetTicketsRequestParamsDTO, GetTicketsResponseDTO } from 'declarations/dto/tickets';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { TicketDetailsI } from 'declarations/interfaces/tickets';
import type { NoopT } from 'declarations/types/common';
import { useFetchTicket } from 'hooks/api/useTickets';

import { useMessages } from '../components/Messages/contexts/MessagesContext';

type UseActionsMenuT = {
  ticketDetails: TicketDetailsI | null;
  isTicketDetailsFetchingError: boolean;
  isLoadingTicketDetails: boolean;
  isTicketReopenPopupOpened: boolean;
  isTicketApprovePopupOpened: boolean;
  isTicketClosePopupOpened: boolean;
  isAddAttachmentPopupOpened: boolean;
  isViewAttachmentsPopupOpened: boolean;
  isChangeClaimStatusPopupOpened: boolean;
  isChangePriorityPopupOpened: boolean;
  isDeleteTicketPopupOpened: boolean;
  selectedTicketId: string | null;
  setTicketId: (id: string | null) => void;
  fetchTicketsParams: GetTicketsRequestParamsDTO;
  handleTabChange: (ticketId: string) => void;
  handleOpenViewAttachmentsPopup: NoopT;
  handleCloseViewAttachmentsPopup: NoopT;
  handleOpenAddAttachmentPopup: NoopT;
  handleCloseAddAttachmentPopup: NoopT;
  handleOpenTicketReopenPopup: NoopT;
  handleCloseTicketReopenPopup: NoopT;
  handleCloseTicketDetailsPopup: NoopT;
  handleOpenTicketApprovePopup: NoopT;
  handleCloseTicketApprovePopup: NoopT;
  handleOpenTicketClosePopup: NoopT;
  handleCloseTicketClosePopup: NoopT;
  handleOpenClaimStatusChangePopup: NoopT;
  handleCloseClaimStatusChangePopup: NoopT;
  handleOpenPriorityChangePopup: NoopT;
  handleClosePriorityChangePopup: NoopT;
  handleOpenDeleteTicketPopup: NoopT;
  handleCloseDeleteTicketPopup: NoopT;
  handleOpenReassignTicketPopup: NoopT;
  handleCloseReassignTicketPopup: NoopT;
  handleOpenShareTicketPopup: NoopT;
  handleCloseShareTicketPopup: NoopT;
};

const useTicketReopenApprovalPopup = (): UseActionsMenuT => {
  /**
   * Hooks
   */
  const [isTicketReopenPopupOpened, setTicketReopenPopupVisibility] = useState(false);
  const [isTicketApprovePopupOpened, setTicketApprovePopupVisibility] = useState(false);
  const [isTicketClosePopupOpened, setTicketClosePopupVisibility] = useState(false);
  const [isAddAttachmentPopupOpened, setAddAttachmentPopupVisibility] = useState(false);
  const [isViewAttachmentsPopupOpened, setViewAttachmentsPopupVisibility] = useState(false);
  const [isChangeClaimStatusPopupOpened, setChangeClaimStatusPopupVisibility] = useState(false);
  const [isChangePriorityPopupOpened, setChangePriorityPopupVisibility] = useState(false);
  const [isDeleteTicketPopupOpened, setDeleteTicketPopupVisibility] = useState(false);
  const {
    setTicketId,
    setSelectedTicketsIds,
    selectedTicketId,
    fetchTicketsParams,
    setShareTicketPopupVisibility,
    setReassignTicketPopupVisibility,
  } = useTickets();
  const { handleFlushPendingMessage, unsavedMessage } = useMessages();
  const queryClient = useQueryClient();

  /**
   * Api hooks
   */
  const ticketDetailsResult = useFetchTicket({
    ticketId: selectedTicketId || '',
    onSuccess: updatedTicket => {
      queryClient.setQueryData<InfiniteData<GetTicketsResponseDTO>>(
        [RequestQueryKeysE.FetchTickets, fetchTicketsParams],
        previousData => {
          return previousData
            ? {
                pageParams: previousData.pageParams,
                pages: previousData.pages.map(page => ({
                  data: page.data.map(ticket => {
                    if (ticket.id === updatedTicket.id && !ticket.isViewed) {
                      return {
                        ...ticket,
                        isViewed: true,
                      };
                    }

                    return ticket;
                  }),
                  count: page.count,
                })),
              }
            : undefined;
        }
      );
    },
  });

  /**
   * Handlers
   */
  const handleCloseTicketDetailsPopup = useCallback(() => {
    setTicketId(null);
    setSelectedTicketsIds([]);

    if (unsavedMessage) {
      handleFlushPendingMessage();
    }
  }, [setTicketId, setSelectedTicketsIds, unsavedMessage, handleFlushPendingMessage]);
  const handleOpenTicketReopenPopup = useCallback(() => {
    setTicketReopenPopupVisibility(true);
  }, []);
  const handleCloseTicketReopenPopup = useCallback(() => {
    setTicketReopenPopupVisibility(false);
  }, []);
  const handleOpenTicketApprovePopup = useCallback(() => {
    setTicketApprovePopupVisibility(true);
  }, []);
  const handleCloseTicketApprovePopup = useCallback(() => {
    setTicketApprovePopupVisibility(false);
  }, []);
  const handleOpenTicketClosePopup = useCallback(() => {
    setTicketClosePopupVisibility(true);
  }, []);
  const handleCloseTicketClosePopup = useCallback(() => {
    setTicketClosePopupVisibility(false);
    queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketsCountByRoofTop]);
  }, [queryClient]);
  const handleOpenAddAttachmentPopup = useCallback(() => {
    setAddAttachmentPopupVisibility(true);
  }, []);
  const handleCloseAddAttachmentPopup = useCallback(() => {
    setAddAttachmentPopupVisibility(false);
  }, []);
  const handleOpenViewAttachmentsPopup = useCallback(() => {
    setViewAttachmentsPopupVisibility(true);
  }, []);
  const handleCloseViewAttachmentsPopup = useCallback(() => {
    setViewAttachmentsPopupVisibility(false);
  }, []);
  const handleOpenClaimStatusChangePopup = useCallback(() => {
    setChangeClaimStatusPopupVisibility(true);
  }, []);
  const handleCloseClaimStatusChangePopup = useCallback(() => {
    setChangeClaimStatusPopupVisibility(false);
  }, []);
  const handleOpenPriorityChangePopup = useCallback(() => {
    setChangePriorityPopupVisibility(true);
  }, []);
  const handleClosePriorityChangePopup = useCallback(() => {
    setChangePriorityPopupVisibility(false);
  }, []);
  const handleOpenDeleteTicketPopup = useCallback(() => {
    setDeleteTicketPopupVisibility(true);
  }, []);
  const handleCloseDeleteTicketPopup = useCallback(() => {
    setDeleteTicketPopupVisibility(false);
  }, []);
  const handleOpenShareTicketPopup = useCallback(() => {
    setShareTicketPopupVisibility(true);
  }, [setShareTicketPopupVisibility]);
  const handleCloseShareTicketPopup = useCallback(() => {
    setShareTicketPopupVisibility(false);
  }, [setShareTicketPopupVisibility]);
  const handleOpenReassignTicketPopup = useCallback(() => {
    setReassignTicketPopupVisibility(true);
  }, [setReassignTicketPopupVisibility]);
  const handleCloseReassignTicketPopup = useCallback(() => {
    setReassignTicketPopupVisibility(false);
  }, [setReassignTicketPopupVisibility]);

  const handleTabChange = useCallback(
    (ticketId: string): void => {
      setTicketId(ticketId);
    },
    [setTicketId]
  );

  return {
    ticketDetails: ticketDetailsResult.data ?? null,
    isTicketDetailsFetchingError: ticketDetailsResult.isError,
    isLoadingTicketDetails: ticketDetailsResult.isLoading || ticketDetailsResult.isRefetching,
    selectedTicketId,
    setTicketId,
    fetchTicketsParams,
    handleTabChange,
    isTicketReopenPopupOpened,
    handleCloseTicketDetailsPopup,
    handleOpenTicketReopenPopup,
    handleCloseTicketReopenPopup,
    isTicketApprovePopupOpened,
    handleOpenTicketApprovePopup,
    handleCloseTicketApprovePopup,
    isTicketClosePopupOpened,
    handleOpenTicketClosePopup,
    handleCloseTicketClosePopup,
    isAddAttachmentPopupOpened,
    handleOpenAddAttachmentPopup,
    handleCloseAddAttachmentPopup,
    isViewAttachmentsPopupOpened,
    handleOpenViewAttachmentsPopup,
    handleCloseViewAttachmentsPopup,
    isChangeClaimStatusPopupOpened,
    handleOpenClaimStatusChangePopup,
    handleCloseClaimStatusChangePopup,
    isChangePriorityPopupOpened,
    handleOpenPriorityChangePopup,
    handleClosePriorityChangePopup,
    isDeleteTicketPopupOpened,
    handleOpenDeleteTicketPopup,
    handleCloseDeleteTicketPopup,
    handleOpenShareTicketPopup,
    handleCloseShareTicketPopup,
    handleOpenReassignTicketPopup,
    handleCloseReassignTicketPopup,
  };
};

export default useTicketReopenApprovalPopup;
